import React from "react";
import {
  Beaker,
  AlertOctagon,
  AlertTriangle,
  ShieldCheck,
  HelpCircle,
  ArrowDownRight,
  ArrowUpRight,
  Search,
} from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Separator } from "./ui/separator";

/**
 * Verdict → { variant, icon, label } for the top-right badge.
 * Reuses the same color variants as the rest of the app for consistency.
 */
function verdictMeta(verdict) {
  switch (verdict) {
    case "honeypot":
      return { variant: "danger", icon: AlertOctagon, label: "Honeypot" };
    case "suspicious":
      return { variant: "warning", icon: AlertTriangle, label: "Suspicious" };
    case "safe":
      return { variant: "success", icon: ShieldCheck, label: "Safe" };
    case "unverified_but_tradeable":
      return { variant: "warning", icon: Search, label: "Unverified" };
    default:
      return { variant: "muted", icon: HelpCircle, label: "Unknown" };
  }
}

function strategyMeta(strategy) {
  if (strategy === "dexscreener-direct")
    return { label: "DexScreener-direct", variant: "primary" };
  if (strategy === "smart")
    return { label: "Smart route", variant: "muted" };
  return null;
}

function fmtPct(value, decimals = 2) {
  if (value === undefined || value === null || isNaN(Number(value))) return "—";
  return `${Number(value).toFixed(decimals)}%`;
}

function fmtSol(lamports) {
  if (lamports === undefined || lamports === null || isNaN(Number(lamports))) return "—";
  return `${(Number(lamports) / 1e9).toFixed(4)} SOL`;
}

function Stat({ label, value, sub, tone }) {
  const toneClass =
    tone === "danger"
      ? "text-[hsl(var(--danger))]"
      : tone === "warning"
      ? "text-[hsl(var(--warning))]"
      : tone === "success"
      ? "text-[hsl(var(--success))]"
      : "";
  return (
    <div className="rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--bg-elev-2))] px-3 py-2">
      <div className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted))]">
        {label}
      </div>
      <div className={`mt-0.5 text-base font-semibold tabular-nums ${toneClass}`}>
        {value}
      </div>
      {sub ? <div className="text-[11px] text-[hsl(var(--muted))]">{sub}</div> : null}
    </div>
  );
}

/**
 * Visual round-trip loss bar — green segment for SOL recovered,
 * red segment for SOL lost over a buy + sell.
 */
function LossBar({ lossPct }) {
  if (typeof lossPct !== "number" || isNaN(lossPct)) return null;
  const loss = Math.max(0, Math.min(100, lossPct));
  const recovered = 100 - loss;
  const tone =
    loss >= 80
      ? "danger"
      : loss >= 30
      ? "warning"
      : "success";
  const recoveredColor =
    tone === "success"
      ? "bg-[hsl(var(--success))]"
      : tone === "warning"
      ? "bg-[hsl(var(--warning))]"
      : "bg-[hsl(var(--muted)/0.5)]";
  return (
    <div className="mt-1">
      <div className="flex h-2 w-full overflow-hidden rounded-full bg-[hsl(var(--muted)/0.15)]">
        <div
          className={`${recoveredColor} transition-all`}
          style={{ width: `${recovered}%` }}
          title={`${recovered.toFixed(1)}% SOL recovered`}
        />
        <div
          className="bg-[hsl(var(--danger))] transition-all"
          style={{ width: `${loss}%` }}
          title={`${loss.toFixed(1)}% lost`}
        />
      </div>
      <div className="mt-1 flex items-center justify-between text-[11px]">
        <span className="inline-flex items-center gap-1">
          <ArrowDownRight
            className={`h-3 w-3 ${
              tone === "success"
                ? "text-[hsl(var(--success))]"
                : tone === "warning"
                ? "text-[hsl(var(--warning))]"
                : "text-[hsl(var(--muted))]"
            }`}
          />
          <span className="text-[hsl(var(--muted))]">Recovered</span>
          <span className="font-medium">{recovered.toFixed(1)}%</span>
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="text-[hsl(var(--muted))]">Lost</span>
          <span className="font-medium text-[hsl(var(--danger))]">{loss.toFixed(1)}%</span>
          <ArrowUpRight className="h-3 w-3 text-[hsl(var(--danger))]" />
        </span>
      </div>
    </div>
  );
}

function ReasonsList({ reasons }) {
  if (!Array.isArray(reasons) || !reasons.length) return null;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted))] mb-1.5">
        Signals
      </div>
      <ul className="space-y-1">
        {reasons.map((r, i) => (
          <li
            key={i}
            className="text-[12px] text-[hsl(var(--fg))] flex items-start gap-2 leading-relaxed"
          >
            <span className="text-[hsl(var(--muted))] mt-0.5">•</span>
            <span>{r}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * HoneypotCard — surfaces the rich honeypot payload from /api/scan.
 * Expects the `honeypot` object as it appears on `report.honeypot`.
 *
 * Shape:
 *   {
 *     verdict: 'safe' | 'suspicious' | 'honeypot' | 'unknown',
 *     buy_out_amount, sell_out_lamports,
 *     round_trip_loss_pct, buy_price_impact_pct, sell_price_impact_pct,
 *     can_sell, test_sol, reasons: string[]
 *   }
 */
export function HoneypotCard({ honeypot }) {
  if (!honeypot) {
    return (
      <Card data-testid="honeypot-card">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Beaker size={16} className="text-[hsl(var(--primary))]" />
            <CardTitle>Honeypot Simulation</CardTitle>
          </div>
          <CardDescription>
            Honeypot simulation unavailable for this scan.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const meta = verdictMeta(honeypot.verdict);
  const Icon = meta.icon;
  const loss = honeypot.round_trip_loss_pct;
  const testSol = honeypot.test_sol ?? 0.1;
  const canSell = honeypot.can_sell;
  const reasons = honeypot.reasons || [];

  const lossTone =
    typeof loss !== "number"
      ? undefined
      : loss >= 80
      ? "danger"
      : loss >= 30
      ? "warning"
      : "success";

  return (
    <Card data-testid="honeypot-card">
      <CardHeader>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Beaker size={16} className="text-[hsl(var(--primary))]" />
            <CardTitle>Honeypot Simulation</CardTitle>
          </div>
          <Badge variant={meta.variant} className="gap-1">
            <Icon size={11} />
            {meta.label}
          </Badge>
        </div>
        <CardDescription>
          Simulates a {testSol} SOL buy and an immediate sell through Jupiter v6 to
          detect tokens that can be bought but not sold, or that bleed value on exit.
          {honeypot.verdict === "unverified_but_tradeable" ? (
            <>
              {" "}Jupiter currently has no route for this mint, but DexScreener
              shows a real market — verify the sell side manually.
            </>
          ) : null}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Route strategy (when relevant) */}
          {(() => {
            const sm = strategyMeta(honeypot.strategy);
            if (!sm) return null;
            return (
              <div className="flex items-center gap-2 text-[11px]">
                <span className="text-[hsl(var(--muted))] uppercase tracking-wider">
                  Route
                </span>
                <Badge variant={sm.variant}>{sm.label}</Badge>
              </div>
            );
          })()}

          {/* Stat tiles */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Stat
              label="Round-trip Loss"
              value={fmtPct(loss, 1)}
              tone={lossTone}
              sub={`on ${testSol} SOL`}
            />
            <Stat
              label="Sell Route"
              value={
                canSell === true
                  ? "Available"
                  : canSell === false
                  ? "Missing"
                  : "—"
              }
              tone={
                canSell === true ? "success" : canSell === false ? "danger" : undefined
              }
            />
            <Stat
              label="Buy Impact"
              value={fmtPct(honeypot.buy_price_impact_pct, 2)}
            />
            <Stat
              label="Sell Impact"
              value={fmtPct(honeypot.sell_price_impact_pct, 2)}
            />
          </div>

          {/* Loss bar */}
          {typeof loss === "number" ? (
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted))] mb-1">
                SOL Recovered vs Lost
              </div>
              <LossBar lossPct={loss} />
            </div>
          ) : null}

          {/* Reasons */}
          {reasons.length ? (
            <>
              <Separator />
              <ReasonsList reasons={reasons} />
            </>
          ) : null}

          {/* Footnote when round-trip number is the SOL out */}
          {honeypot.sell_out_lamports != null ? (
            <div className="text-[11px] text-[hsl(var(--muted))] font-mono">
              Selling the bought tokens would return ~{fmtSol(honeypot.sell_out_lamports)}.
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
